// @ts-nocheck
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { useEventService } from '@/modules/events/hooks/useEventService';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { parseLocalDate } from '@/utils/formatters';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign,
  Edit,
  Eye,
  Globe,
  Loader2
} from 'lucide-react';

export default function AdminEventDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { organization } = useOrganizationContext();
  const organizationId = organization?.id || null;
  const eventService = useEventService(organizationId);
  
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvent();
  }, [slug, eventService]);

  const loadEvent = async () => {
    if (!eventService || !slug) return;

    try {
      setLoading(true);
      const data = await eventService.getBySlug(slug);
      setEvent(data);
    } catch (error) {
      console.error('Failed to load event:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEventStatus = (event: any) => {
    const now = new Date();
    const start = parseLocalDate(event.start_date);
    const end = parseLocalDate(event.end_date);
    
    if (now > end) return { label: 'Past', variant: 'secondary' as const };
    if (now < start && event.is_published) return { label: 'Upcoming', variant: 'default' as const };
    if (now >= start && now <= end) return { label: 'Live', variant: 'destructive' as const };
    if (!event.is_published) return { label: 'Draft', variant: 'outline' as const };
    return { label: 'Scheduled', variant: 'default' as const };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold mb-2">Event not found</h3>
        <p className="text-muted-foreground mb-4">
          The event you're looking for doesn't exist or has been removed.
        </p>
        <Button onClick={() => navigate('/modular-dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Button>
      </div>
    );
  }

  const status = getEventStatus(event);
  const location = [event.venue_city, event.venue_state].filter(Boolean).join(', ');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/modular-dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => {
              if (!event.slug) {
                toast.error('Event must have a slug to view public page');
                return;
              }
              if (!event.is_published) {
                toast.info('This is a draft event - public page may not display correctly until published');
              }
              window.open(`/events/${event.slug}`, '_blank');
            }}
            disabled={!event.slug}
          >
            <Eye className="h-4 w-4 mr-2" />
            View Public Page
          </Button>
          <Button onClick={() => navigate(`/admin-dashboard/events/${event.id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Event
          </Button>
        </div>
      </div>

      {/* Event Info */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold">{event.name}</h1>
          <Badge variant={status.variant}>{status.label}</Badge>
          {event.is_published ? (
            <Badge variant="outline">
              <Globe className="h-3 w-3 mr-1" />
              Published
            </Badge>
          ) : (
            <Badge variant="secondary">Draft</Badge>
          )}
        </div>
        {event.description && (
          <p className="text-muted-foreground">{event.description}</p>
        )}
      </div>

      <Separator />

      {/* Details Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Start Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {parseLocalDate(event.start_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              End Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {parseLocalDate(event.end_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </CardContent>
        </Card>

        {location && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{location}</div>
              {event.venue_name && (
                <div className="text-sm text-muted-foreground mt-1">
                  {event.venue_name}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {event.registration_count || 0}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {event.registration_type || 'Individual'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Info */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Registration Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Online Registration:</span>
              <span className="font-medium">
                {event.accept_online_registrations ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {event.registration_open_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Opens:</span>
                <span className="font-medium">
                  {new Date(event.registration_open_date).toLocaleDateString()}
                </span>
              </div>
            )}
            {event.registration_close_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Closes:</span>
                <span className="font-medium">
                  {new Date(event.registration_close_date).toLocaleDateString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Event Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Event Format:</span>
              <span className="font-medium capitalize">
                {event.event_format || 'Standard'}
              </span>
            </div>
            {event.gender && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gender:</span>
                <span className="font-medium capitalize">{event.gender}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Slug:</span>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {event.slug}
              </code>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
